import { useEffect, useMemo, useState } from "react";
import { requisitionList, requisitionRaisePhaseFlag, type RequisitionListOutput } from "@/api/tools";
import { inputLengthHint, inputMaxLength } from "@/api/inputLimits";
import { RequisitionWaiting } from "@/ui/RequisitionWaiting";
import { useRead, useWrite } from "@/api/useTool";
import { useSession } from "@/session/useSession";
import { useViewState } from "@/shell/viewState";
import { useRoute } from "@/router";
import { LEVEL_LABEL } from "@/ui/vocabulary";
import { FilterRow } from "@/ui/FilterRow";
import { Banner, Empty, Field, Pager, PhasePill, ReasonDialog, Skeleton, Table, fmtDate } from "@/ui";

// V1-INTERACTIONS §4.1.4 — Phase targets: tiles, the table with the phase pill, and the one direct control (Send flag).

const PHASES = ["posting_to_sourcing", "sourcing_to_first_screen", "first_screen_to_hm", "hm_to_onsite", "onsite_to_offer", "offer_to_acceptance", "total"] as const;
export const PHASE_LABEL: Record<string, string> = { posting_to_sourcing: "Posting → Sourcing", sourcing_to_first_screen: "Sourcing → First screen", first_screen_to_hm: "First screen → Hiring Manager interview", hm_to_onsite: "Hiring Manager interview → Onsite", onsite_to_offer: "Onsite → Offer", offer_to_acceptance: "Offer → Acceptance", total: "Time to Acceptance" };

export function PhaseTargets() {
  const { can, manifest } = useSession();
  const pendingLimit = inputMaxLength(manifest, "requisition.raise_phase_flag", "pending_action");
  const { register } = useViewState();
  const route = useRoute();
  const filters = new URLSearchParams(route.raw.split("?")[1] ?? "");
  const state = filters.get("phase") ?? "";
  const level = filters.get("level") ?? "";
  const owner = filters.get("owner") === "mine" ? "mine" : "anyone";
  const page = Math.max(1, Math.floor(Number(filters.get("page"))) || 1);
  const setFilter = (name: string, value: string) => {
    const next = new URLSearchParams(window.location.hash.split("?")[1] ?? "");
    if (value) next.set(name, value); else next.delete(name);
    if (name !== "page") next.delete("page");
    const query = next.toString();
    window.location.hash = `#phase-targets${query ? `?${query}` : ""}`;
  };
  const setState = (value: string) => setFilter("phase", value);
  const filtered = !!(state || level || owner === "mine");
  const [flagFor, setFlagFor] = useState<(Extract<RequisitionListOutput["items"][number], { waiting_on: unknown }> & { pending: string }) | null>(null);
  const q = useRead(["requisitions", "phase-targets", state, level, owner, page], () => requisitionList({ phase_report: true, page, page_size: 25, sort: "elapsed", owner, ...(level ? { level } : {}), ...(state ? { phase_state: state as "behind" } : {}) }));
  const send = useWrite((i: { requisition_id: string; pending_action: string; message?: string | null }, k) => requisitionRaisePhaseFlag(i, k), [["requisitions"], ["today"]]);
  useEffect(() => register({ view: "phase-targets", ids: {}, summary: `phase targets · ${q.data?.total ?? "?"} rows` }), [register, q.data?.total]);
  const rows = useMemo(() => (q.data?.items ?? []).filter((r) => "phase" in r).filter((r) => r.phase.phase_key), [q.data]);
  const counts = q.data?.phase_counts ?? {};
  const onClocks = Object.values(counts).reduce((total, count) => total + count, 0);
  const canFlag = (r: typeof rows[number]) => "can_send_phase_flag" in r && r.can_send_phase_flag && can("requisition.raise_phase_flag") && (r.phase.state === "trending_behind" || r.phase.state === "behind");
  const showAction = rows.some(canFlag);
  return (
    <div>
      <h1 className="page">Phase targets</h1>
      <p className="lede">Targets by level from the Process Guide; a phase turns amber at 80% of its target and reads Behind past it. Targets guide, they never block.</p>
      <div className="grid grid--kpi">
        <div className="card kpi">
          <b>{onClocks}</b>
          <span>requisitions on a phase clock</span>
        </div>
        <button className="card kpi kpi--link" onClick={() => setState("on_track")}>
          <b>{counts.on_track ?? 0}</b>
          <span>on track</span>
        </button>
        <button className="card kpi kpi--link" onClick={() => setState("trending_behind")}>
          <b>{counts.trending_behind ?? 0}</b>
          <span>trending behind</span>
        </button>
        <button className="card kpi kpi--link" onClick={() => setState("behind")}>
          <b>{counts.behind ?? 0}</b>
          <span>past target</span>
        </button>
      </div>
      <FilterRow>
        <label className="filter"><span>Level</span><select value={level} onChange={event => setFilter("level", event.target.value)}><option value="">Any</option>{Object.entries(LEVEL_LABEL).map(([value, label]) => <option key={value} value={value}>{label}</option>)}</select></label>
        <label className="filter">
          <span>Phase state</span>
          <select value={state} onChange={(e) => setState(e.target.value)}>
            <option value="">Any</option>
            <option value="on_track">On track</option>
            <option value="trending_behind">Trending behind</option>
            <option value="behind">Behind</option>
          </select>
        </label>
        <label className="filter"><span>Owner</span><select value={owner} onChange={event => setFilter("owner", event.target.value === "anyone" ? "" : event.target.value)}><option value="anyone">Anyone</option><option value="mine">Mine</option></select></label>
        {filtered && <button className="btn btn--sm" onClick={() => { window.location.hash = "#phase-targets"; }}>Clear filters</button>}
      </FilterRow>
      <p role="status" className="muted">{q.isLoading ? "Loading phase clocks…" : `${q.data?.total ?? 0} matching phase ${q.data?.total === 1 ? "clock" : "clocks"}`}</p>
      {send.isSuccess && <p role="status" className="callout callout--ok">Phase flag sent to the Hiring Manager and the HR Business Partner. Receipt {send.data.receipt_id}.</p>}
      {q.isError && <Banner error={q.error} onRetry={() => void q.refetch()} />}
      {q.isLoading && <Skeleton />}
      {q.data && rows.length === 0 && <Empty level={2} heading="No matching phase clocks">No requisition is on a phase clock{filtered ? " with these filters" : ""}. Clocks start when a position is posted.</Empty>}
      {rows.length > 0 && (
        <Table
          label="Phase targets" mobile="stack"
          head={
            <tr>
              <th className="sticky-l">Requisition</th>
              <th>Level</th>
              <th>Current phase</th>
              <th>Target</th>
              <th>Elapsed</th>
              <th>Trend</th>
              <th>Waiting on</th>
              {showAction && <th className="sticky-r">Action</th>}
            </tr>
          }
        >
          {rows.map((r) => (
            <tr key={r.id}>
              <td className="sticky-l">
                <a href={`#requisitions/${r.id}`}>
                  <span className="mono">{r.id}</span> {r.masked ? <i>{r.title}</i> : r.title}
                </a>
              </td>
              <td>{r.level_label}</td>
              <td>{r.phase.label}</td>
              <td>{r.phase.target_days ?? "—"} d</td>
              <td>
                {r.phase.active_days ?? r.phase.elapsed_days} d · Calendar Time {r.phase.calendar_days ?? "—"} · Days on Hold {r.phase.held_days}
              </td>
              <td>
                <div className="trend" aria-hidden="true">
                  <div className={`trend__bar trend__bar--${r.phase.state}`} style={{ width: `${Math.min(100, r.phase.target_days ? ((r.phase.elapsed_days ?? 0) / r.phase.target_days) * 100 : 0)}%` }} />
                </div>
                <PhasePill phase={r.phase} />
              </td>
              <td>{"waiting_on" in r ? <RequisitionWaiting {...r} /> : "Restricted"}</td>
              {showAction && <td className="sticky-r">
                {canFlag(r) && "waiting_on" in r && (
                  <button className="btn btn--sm btn--primary" onClick={() => { send.reset(); setFlagFor({ ...r, pending: r.waiting_on[0]?.title ?? "" }); }}>
                    Send flag
                  </button>
                )}
              </td>}
            </tr>
          ))}
        </Table>
      )}
      {q.data && <Pager page={page} count={rows.length} busy={q.isFetching} previous={page > 1 ? () => setFilter("page", String(page - 1)) : undefined} next={page * q.data.page_size < q.data.total ? () => setFilter("page", String(page + 1)) : undefined} />}
      <h2 className="sec" style={{ marginTop: 18 }}>
        Targets by level (calendar days, from configuration)
      </h2>
      {q.data?.phase_targets && <p className="muted">Published configuration v{q.data.phase_targets.version} · effective {fmtDate(q.data.phase_targets.effective_from)}. Existing phase clocks retain the target set when they started.</p>}
      {q.data && !q.data.phase_targets && <p className="muted">The published phase-target configuration is unavailable.</p>}
      {q.data?.phase_targets && <Table
        label="Targets by level"
        head={
          <tr>
            <th className="sticky-l">Phase</th>
            <th>Manager & Below</th>
            <th>Director & Sr Director</th>
            <th>VP & Above</th>
          </tr>
        }
      >
        {PHASES.map((p) => (
          <tr key={p}>
            <td className="sticky-l">{PHASE_LABEL[p]}</td>
            {(["manager_and_below", "director_srdirector", "vp_and_above"] as const).map((lvl) => (
              <td key={lvl}>{q.data.phase_targets?.levels[lvl]?.[p] === undefined ? "Unavailable" : `${q.data.phase_targets.levels[lvl]![p]} d`}</td>
            ))}
          </tr>
        ))}
      </Table>}
      <ReasonDialog title={`Send the phase flag — ${flagFor?.title ?? ""}`} label="Message to the Hiring Manager and the HR Business Partner (optional context)" optional confirm="Send" open={!!flagFor} onClose={() => setFlagFor(null)} busy={send.isPending} error={send.error} onConfirm={(message) => flagFor && void send.mutateAsync({ requisition_id: flagFor.id, pending_action: flagFor.pending.trim(), message }).then(() => setFlagFor(null))}
        preview={message => <section aria-label="Message preview" className="callout"><h3 className="sec">Message preview</h3><p>{flagFor?.phase.label || "phase"} · day {flagFor?.phase.elapsed_days} of {flagFor?.phase.target_days} · pending: {flagFor?.pending.trim() || "Enter the pending action above"}{message ? ` — ${message}` : ""}</p></section>}>
        <Field label="Pending action" required id="flag-pending" hint={<>{flagFor?.waiting_count ? "From the recorded waiting work; review before sending." : "No waiting item is recorded. Name who the phase is waiting on and for what."} {inputLengthHint(flagFor?.pending ?? "", pendingLimit)}</>}>
          <input id="flag-pending" value={flagFor?.pending ?? ""} minLength={2} maxLength={pendingLimit} pattern={".*\\S.*\\S.*"} onChange={(e) => flagFor && setFlagFor({ ...flagFor, pending: e.target.value })} placeholder="e.g. resumes awaiting the Hiring Manager" />
        </Field>
        <p className="muted">Recipients are fixed: the Hiring Manager and the HR Business Partner. The flag is repeated once if unacknowledged after two business days.</p>
      </ReasonDialog>
    </div>
  );
}
